import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Cancel, Delete } from "@mui/icons-material";
import { useAuth } from "../AuthContext";
import Slider from "react-slick";
import "../ProjectManage/slider-custom.css";
import loadingGif from "../loading.gif";
import ReactQuill from "react-quill"; // Import ReactQuill
import "react-quill/dist/quill.snow.css"; // Import Quill styles

// Define consistent toolbar options
const toolbarOptions = [
  [{ font: [] }],
  [{ size: [] }],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["image"],
];

const AllPost = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [updatedTitle, setUpdatedTitle] = useState("");
  const [updatedContent, setUpdatedContent] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null); // Track expanded post
  const [openModal, setOpenModal] = useState(false); // Modal state
  const { token, uid } = useAuth();

  useEffect(() => {
    fetchPosts();
  }, [token]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get("https://sirius.runasp.net/api/Post", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPosts(response.data);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://sirius.runasp.net/api/Post/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPosts(posts.filter((post) => post.id !== id));
    } catch (error) {
      console.error("There was an error deleting the post!", error);
      setError("Failed to delete the post.");
    }
  };

  const handleEdit = async (id) => {
    try {
      const formData = new FormData();
      formData.append("title", updatedTitle);
      formData.append("content", updatedContent);
      formData.append("userId", uid);

      if (selectedFile) {
        formData.append("photos", selectedFile);
      }

      await axios.put(`https://sirius.runasp.net/api/Post/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      fetchPosts(); // Fetch updated posts after editing
      handleCloseModal(); // Close the modal
    } catch (error) {
      console.error(
        "There was an error updating the post!",
        error.response?.data || error.message
      );
      setError(
        `Failed to update the post. Error: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const handleImageDelete = async (postId, imageFilePath) => {
    try {
      await axios.delete("https://sirius.runasp.net/api/post/Photo", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          postId: postId,
          filePath: imageFilePath,
        },
      });
      fetchPosts();
    } catch (error) {
      console.error("There was an error deleting the image!", error);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const toggleExpand = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  const handleOpenModal = (post) => {
    setEditingPost(post.id);
    setUpdatedTitle(post.title);
    setUpdatedContent(post.content);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPost(null);
    setSelectedFile(null);
  };

  const settings = (photoCount) => ({
    dots: photoCount > 1,
    infinite: photoCount > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    appendDots: (dots) => (
      <div style={{ padding: "10px" }}>
        <ul style={{ margin: "0px", padding: "0px" }}> {dots} </ul>
      </div>
    ),
    customPaging: (i) => (
      <div
        className="custom-dot"
        style={{
          width: "10px",
          height: "10px",
          background: "lightcoral",
          borderRadius: "50%",
          display: "inline-block",
          margin: "10px 5px 0 5px",
        }}
      />
    ),
    arrows: false,
  });

  if (loading) {
    return (
      <div
        style={{
          margin: "50px auto",
          textAlign: "center",
          width: "200px",
          height: "200px",
        }}
      >
        <img width={"100%"} src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  if (error) return <p>Error: {error}</p>;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "1rem",
        flexDirection: "row",
        justifyContent: "center",
      }}
    >
      {posts.map((post) => (
        <Card key={post.id} style={{ width: "400px", marginBottom: "1rem" }}>
          <CardContent>
            <Typography variant="h6" style={{ marginBottom: "2rem" }}>
              {post.title}
            </Typography>
            <Typography
              variant="body2"
              style={{ marginBottom: "1rem", cursor: "pointer" }}
              onClick={() => toggleExpand(post.id)}
              dangerouslySetInnerHTML={{
                __html:
                  expandedPost === post.id
                    ? post.content
                    : `${post.content.substring(0, 100)}...`,
              }}
            />
            <Button
              variant="text"
              onClick={() => toggleExpand(post.id)}
              sx={{
                color: "#e84545", // Set your desired background color here
                "&:hover": {
                  color: "#c73b3b", // Optional: Set background color on hover
                },
              }}
            >
              {expandedPost === post.id ? "Show Less" : "Read More"}
            </Button>
            <Slider {...settings(post.photos ? post.photos.length : 0)}>
              {post.photos &&
                post.photos.map((photo, index) => (
                  <div key={index}>
                    <img
                      src={`https://sirius.runasp.net/${photo.filePath}`}
                      alt={`Post ${index}`}
                      style={{
                        width: "100%",
                        height: "300px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                ))}
            </Slider>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            >
              <IconButton
                onClick={() => handleOpenModal(post)}
                color="primary"
                sx={{
                  color: "#e84545", // Set your desired background color here
                  "&:hover": {
                    color: "#c73b3b", // Optional: Set background color on hover
                  },
                }}
              >
                <Edit />
              </IconButton>
              <IconButton onClick={() => handleDelete(post.id)} color="error">
                <Delete />
              </IconButton>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Modal for editing posts */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Post</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Post Title"
            value={updatedTitle}
            onChange={(e) => setUpdatedTitle(e.target.value)}
            style={{ marginBottom: "2rem" }}
          />
          <ReactQuill
            value={updatedContent}
            onChange={(content, delta, source, editor) =>
              setUpdatedContent(editor.getHTML())
            }
            theme="snow"
            modules={{ toolbar: toolbarOptions }}
            style={{ height: "200px", marginBottom: "2rem" }}
          />
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ margin: "4rem 0 1rem 0" }}
          />
          {editingPost &&
            posts.find((post) => post.id === editingPost)?.photos?.length >
              0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "1rem",
                  marginTop: "1rem",
                }}
              >
                {posts
                  .find((post) => post.id === editingPost)
                  .photos.map((photo, index) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        width: "100px",
                        height: "100px",
                      }}
                    >
                      <img
                        src={`https://sirius.runasp.net/${photo.filePath}`}
                        alt={`Post ${index}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <IconButton
                        size="small"
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          backgroundColor: "rgba(0,0,0,0.5)",
                        }}
                        onClick={() =>
                          handleImageDelete(editingPost, photo.filePath)
                        }
                      >
                        <Cancel fontSize="small" style={{ color: "white" }} />
                      </IconButton>
                    </div>
                  ))}
              </div>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={() => handleEdit(editingPost)} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AllPost;
